import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import AdminPageHeader from "./components/AdminPageHeader";
import AdminStatCard from "./components/AdminStatCard";
import { AdminCategoryChart, AdminTrafficChart } from "./components/AdminCharts";
import { useAuth } from "@/context/AuthContext";
import {
  getAdminOverview,
  getAdminTrend,
  getAdminCategoryStats,
  getAdminActivity,
  getPendingApprovals,
  type AdminActivity,
} from "@/services/adminApi";
import type { AdminCategoryStat, AdminOverview, AdminTrendPoint } from "@/types";

const toneClasses: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function AdminDashboard() {
  const { token } = useAuth();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [trend, setTrend] = useState<AdminTrendPoint[]>([]);
  const [categoryStats, setCategoryStats] = useState<AdminCategoryStat[]>([]);
  const [activity, setActivity] = useState<AdminActivity[]>([]);
  const [approvals, setApprovals] = useState<Awaited<ReturnType<typeof getPendingApprovals>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([
      getAdminOverview(token),
      getAdminTrend(token),
      getAdminCategoryStats(token),
      getAdminActivity(token),
      getPendingApprovals(token),
    ])
      .then(([o, t, c, a, p]) => {
        if (!active) return;
        setOverview(o);
        setTrend(t);
        setCategoryStats(c);
        setActivity(a);
        setApprovals(p);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ))}
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center">
        <i className="ri-error-warning-line text-2xl text-secondary-600"></i>
        <p className="mt-2 text-sm text-secondary-900">We couldn't load the admin dashboard right now.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-refresh-line"></i> Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <Seo title="Admin Dashboard" path="/admin" />

      <AdminPageHeader
        icon="ri-shield-keyhole-line"
        title="Admin dashboard"
        subtitle="A live overview of listings, users, revenue and moderation across the platform."
        actions={
          <>
            <Link
              to="/admin/approvals"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-checkbox-circle-line"></i> Approvals
              {approvals.length > 0 && (
                <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-bold text-secondary-900">
                  {approvals.length}
                </span>
              )}
            </Link>
            <Link
              to="/admin/businesses"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-store-2-line"></i> Manage businesses
            </Link>
          </>
        }
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <AdminStatCard label="Total businesses" value={overview.totalBusinesses.toLocaleString()} icon="ri-store-2-line" change={overview.businessChange} hint="vs last month" tone="primary" />
        <AdminStatCard label="Pending approvals" value={String(overview.pendingApprovals)} icon="ri-time-line" hint="awaiting review" tone="secondary" />
        <AdminStatCard label="Monthly revenue" value={`₹${overview.monthlyRevenue.toLocaleString("en-IN")}`} icon="ri-money-rupee-circle-line" change={overview.revenueChange} hint="vs last month" tone="accent" />
        <AdminStatCard label="Registered users" value={overview.totalUsers.toLocaleString()} icon="ri-group-line" hint={`${overview.activeSubscriptions} subscriptions`} tone="neutral" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
        <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-base font-bold text-foreground-950">Platform traffic</h2>
              <p className="mt-0.5 text-xs text-foreground-500">Views &amp; live listings over 10 months</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1.5 text-foreground-600">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-primary-500"></span> Views
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground-600">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent-500"></span> Listings
              </span>
            </div>
          </div>
          <div className="mt-4">
            <AdminTrafficChart data={trend} />
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">Recent activity</h2>
          <div className="mt-4 space-y-3.5">
            {activity.slice(0, 6).map((a) => (
              <div key={a.id} className="flex items-start gap-3">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${toneClasses[a.tone] ?? toneClasses.primary}`}>
                  <i className={`${a.icon} text-base`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug text-foreground-900">{a.text}</p>
                  <p className="mt-0.5 text-[11px] text-foreground-500">{a.meta}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-foreground-950">Listings by category</h2>
            <Link to="/admin/analytics" className="text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
              Full analytics
            </Link>
          </div>
          <div className="mt-4">
            <AdminCategoryChart data={categoryStats} />
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-foreground-950">Pending approvals</h2>
            <Link to="/admin/approvals" className="text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
              Review all
            </Link>
          </div>
          <div className="mt-4 space-y-3">
            {approvals.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-md border border-background-200 p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-950">{p.name}</p>
                  <p className="truncate text-xs text-foreground-500">
                    {p.categoryName} · {p.locationName}
                  </p>
                </div>
                <StatusBadge status="pending" />
                <Link
                  to="/admin/approvals"
                  className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                >
                  Review
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}